const SECOND = 1000;
const MIN = 60 * SECOND;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/**
 * Enum of temporal granularity for time-series readings.
 *
 * Granularity is limited to a day of data, and only includes divisions which are factors of a day. (no remainder)
 * @readonly
 * @enum {number}
 */
export const GRANULARITY = Object.freeze({
	Day: DAY,
	SixHours: 6 * HOUR,
	ThreeHours: 3 * HOUR,
	Hour: HOUR,
	HalfHour: 30 * MIN,
	Default: 15 * MIN, // default for roll-ups
	Raw: 0, // default for individual events
});

/**
 * Date interval for bulk stream readings
 * @typedef {Array} DateRange
 * @property {string} 0 - Start of the interval as a UTC date ex: 2006-01-02
 * @property {string} 1 - Exclusive end of the interval as a UTC date ex: 2006-01-02
 */

/** Retrieves all streams having data for a date-range. (cached) */
export default class ReadingsByDate {
	#streamMgr;

	constructor(streamMgr) {
		this.#streamMgr = streamMgr;
	}

	/**
	 * Get all stream readings for a date-range.
	 * @param {DateRange} range Date interval for bulk stream readings
	 * @param {GRANULARITY} granularity Desired temporal granularity (binning) of TS readings.
	 * @param {boolean?} forceRefresh Forces a refresh of the requested range.
	 * @param {Array?} streamsFilter list of streams to get readings for
	 */
	async getReadings(range, granularity, forceRefresh, streamsFilter = []) {
		const streamIds = await this.#streamMgr.getStreamIds();
		if (!streamIds.length) return [];

		// defaultModel is already waited for in getStreamIds
		const model = this.#streamMgr.defaultModel;
		return model.asyncPropertyOperation({
			"operation": "GET_BULK_READINGS",
			"range": range,
			"granularity": granularity,
			"forceRefresh": forceRefresh,
			"filter": streamsFilter.length ? { elementIds: streamsFilter } : undefined,
		});
	}

	/**
	 * Get all transition data for a date-range.
	 * @param {DateRange} range Date interval for bulk stream readings
	 * @param {boolean?} forceRefresh Forces a refresh of the requested range.
	 * @param {Array?} streamsFilter list of streams to get readings for
	 */
	async getTransitionData(range, forceRefresh, streamsFilter = []) {
		const streamIds = await this.#streamMgr.getStreamIds();
		if (!streamIds.length) return [];

		// defaultModel is already waited for in getStreamIds
		const model = this.#streamMgr.defaultModel;
		return model.asyncPropertyOperation({
			"operation": "GET_BULK_TRANSITION_DATA",
			"range": range,
			"forceRefresh": forceRefresh,
			"filter": streamsFilter.length ? { elementIds: streamsFilter } : undefined,
		});
	}


	/**
	 * Get all connections' named-state history for a range, as periods.
	 *
	 * optimization: can be made into a dense transferable buffer if/when needed.
	 *
	 * @param {[number, number]} range `[fromMs, toMs)`, exclusive end
	 * @param {Array?} streamsFilter list of streams to get states for
	 * @param {number?} mask Bitmask to consider; omit for the full state bitmask (e.g. THRESHOLD_ALERTS_MASK )
	 * @returns {Promise<StatePeriod[]>}
	 */
	async getStatesForRange(range, streamsFilter = [], mask) {
		const streamIds = await this.#streamMgr.getStreamIds();
		if (!streamIds.length) return [];

		const model = this.#streamMgr.defaultModel;
		return model.asyncPropertyOperation({
			"operation": "GET_STATES_FOR_RANGE",
			"range": range,
			"filter": streamsFilter.length ? { elementIds: streamsFilter } : undefined,
			"mask": mask,
		});
	}
}
